import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import { useNotify, useRecordContext } from 'react-admin';
import { API_BASE_URL, authFetch } from '../../apiClient.ts';
import {
  downloadBlob,
  getErrorMessage,
  parseEmailList
} from '../../utils/helperUtils.ts';

type OpportunityRecord = { id?: number; dealName?: string | null };

// Save or send the opportunity profile as a PDF. The document is built on the
// server so the download and the emailed copy are the same file.
export const ProfilePdfActions: React.FC = () => {
  const record = useRecordContext<OpportunityRecord>();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!record?.id) return null;

  const dealName = record.dealName ?? `Opportunity #${record.id}`;

  const handleDownload = async () => {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/opportunity-profiles/${record.id}/pdf`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      downloadBlob(await response.blob(), `${dealName}-profile.pdf`);
    } catch (error) {
      notify(getErrorMessage(error), { type: 'error' });
    }
  };

  const openDialog = () => {
    setSubject(`Opportunity Profile: ${dealName}`);
    setMessage(`The opportunity profile for ${dealName} is attached.`);
    setOpen(true);
  };

  const handleSend = async () => {
    const { emails, invalid } = parseEmailList(to);
    if (invalid.length > 0) {
      notify(`Not a valid email: ${invalid.join(', ')}`, { type: 'warning' });
      return;
    }
    if (emails.length === 0) {
      notify('Enter at least one recipient', { type: 'warning' });
      return;
    }

    setSending(true);
    try {
      const response = await authFetch(
        `${API_BASE_URL}/opportunity-profiles/${record.id}/email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emails, title: subject, body: message })
        }
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      notify('Profile sent', { type: 'success' });
      setOpen(false);
      setTo('');
    } catch (error) {
      notify(getErrorMessage(error), { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
        Download PDF
      </Button>
      <Button
        variant="outlined"
        startIcon={<EmailIcon />}
        sx={{ ml: 1 }}
        onClick={openDialog}
      >
        Email PDF
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Email Opportunity Profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="To"
              placeholder="alice@example.com, bob@example.com"
              fullWidth
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <TextField
              label="Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSend} disabled={sending}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
